package com.rajen.angrejidictionary.presentation.viewmodel

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.rajen.angrejidictionary.analytics.Umami
import com.rajen.angrejidictionary.data.local.AdminAuthManager
import com.rajen.angrejidictionary.data.remote.AdminApi
import com.rajen.angrejidictionary.data.remote.WordApi
import com.rajen.angrejidictionary.data.remote.dto.SuggestionItem
import io.ktor.client.plugins.ClientRequestException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

class SuggestionsViewModel {

    private val scope = CoroutineScope(Dispatchers.Default)

    var suggestions by mutableStateOf<List<SuggestionItem>>(emptyList())
        private set
    var totalSuggestions by mutableStateOf(0L)
        private set
    var currentPage by mutableStateOf(1)
        private set
    var isLoading by mutableStateOf(false)
        private set
    var error by mutableStateOf<String?>(null)
        private set
    var selectedStatus by mutableStateOf<String?>(null)
        private set
    var actionMessage by mutableStateOf<String?>(null)
        private set
    var forceLogout by mutableStateOf(false)
        private set

    // Count of pending suggestions regardless of the currently selected filter —
    // drives the notification badge on the "Suggestions" tab.
    var pendingCount by mutableStateOf(0L)
        private set

    // Id of the suggestion currently being checked against the dictionary —
    // drives the "⏳ Checking…" state on that row's Approve button.
    var checkingWordId by mutableStateOf<String?>(null)
        private set

    // Set when Approve is clicked and the suggested word isn't in the
    // dictionary yet. AdminDashboardPage watches this, redirects to the Add
    // Word tab, and links the two together so the suggestion gets approved
    // automatically once the word is saved. Consumed immediately after the
    // redirect fires — see consumePendingWordAdd().
    var pendingWordAddSuggestion by mutableStateOf<SuggestionItem?>(null)
        private set

    init {
        loadSuggestions()
        refreshPendingCount()
    }

    fun loadSuggestions(page: Int = 1, status: String? = selectedStatus) {
        isLoading = true
        error = null
        selectedStatus = status
        Umami.trackAdminSuggestionsView(page, status)

        scope.launch {
            try {
                val response = AdminApi.getSuggestions(page, 20, status)
                if (response.success && response.data != null) {
                    // Newest submissions first, independent of whatever order the
                    // backend happens to return them in.
                    suggestions = response.data.suggestions.sortedByDescending { it.createdAt }
                    totalSuggestions = response.data.total
                    currentPage = page
                } else {
                    error = response.error?.message ?: "Failed to load suggestions"
                }
            } catch (e: Exception) {
                handleError(e)
            } finally {
                isLoading = false
            }
        }
    }

    /** Refreshes the pending-suggestions count used for the tab badge. Non-fatal on failure. */
    fun refreshPendingCount() {
        scope.launch {
            try {
                val response = AdminApi.getSuggestions(page = 1, pageSize = 1, status = "pending")
                if (response.success && response.data != null) {
                    pendingCount = response.data.total
                }
            } catch (_: Exception) {
                // Badge just won't update this cycle; not worth surfacing an error for.
            }
        }
    }

    /**
     * Approve is no longer a plain status flip: check whether the suggested
     * word is already in the dictionary first (public /items/word/{term}
     * lookup, same one word pages use — no admin token needed).
     * - Found  -> approve immediately, same as before.
     * - Not found (404) -> set pendingWordAddSuggestion so the dashboard can
     *   redirect to Add Word and link the two together.
     * - Anything else (network/server error) -> surface an error, don't guess.
     */
    fun onApproveClick(suggestion: SuggestionItem) {
        checkingWordId = suggestion.id

        scope.launch {
            try {
                val term = suggestion.suggestedWord.trim().lowercase()
                val response = WordApi.getWordByTerm(term)
                if (response.success && response.data != null) {
                    updateStatus(suggestion.id, "approved")
                } else {
                    pendingWordAddSuggestion = suggestion
                }
            } catch (e: ClientRequestException) {
                // 404 — word isn't in the dictionary yet.
                pendingWordAddSuggestion = suggestion
            } catch (e: Exception) {
                actionMessage = "❌ Couldn't check if this word already exists — try again."
            } finally {
                checkingWordId = null
            }
        }
    }

    fun consumePendingWordAdd() {
        pendingWordAddSuggestion = null
    }

    fun updateStatus(id: String, status: String) {
        scope.launch {
            try {
                val response = AdminApi.updateSuggestionStatus(id, status)
                if (response.success) {
                    actionMessage = "✅ Status updated to '$status'"
                    Umami.trackAdminSuggestionAction(status, id)
                    loadSuggestions(currentPage, selectedStatus)
                    refreshPendingCount()
                } else {
                    actionMessage = "❌ ${response.error?.message ?: "Failed to update"}"
                }
            } catch (e: Exception) {
                handleError(e)
                actionMessage = "❌ Network error"
            }
        }
    }

    fun deleteSuggestion(id: String) {
        scope.launch {
            try {
                val response = AdminApi.deleteSuggestion(id)
                if (response.success) {
                    actionMessage = "✅ Suggestion deleted"
                    Umami.trackAdminSuggestionAction("delete", id)
                    loadSuggestions(currentPage, selectedStatus)
                    refreshPendingCount()
                } else {
                    actionMessage = "❌ ${response.error?.message ?: "Failed to delete"}"
                }
            } catch (e: Exception) {
                handleError(e)
                actionMessage = "❌ Network error"
            }
        }
    }

    fun clearActionMessage() {
        actionMessage = null
    }

    /** Manual refresh — reloads whatever page/filter is currently showing, plus the badge count. */
    fun refresh() {
        loadSuggestions(currentPage, selectedStatus)
        refreshPendingCount()
    }

    private fun handleError(e: Exception) {
        val msg = e.message ?: ""
        if (msg.contains("401") || msg.contains("Unauthorized")) {
            AdminAuthManager.logout()
            forceLogout = true
        } else {
            error = "Network error: $msg"
        }
    }
}